import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Shield, Users, Clock, Award, MapPin, FileCheck, ArrowRight, Quote, Star } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import satAirportImg from "@/assets/sat-airport-entrance.jpeg";

const militaryTestimonials = [
  {
    name: "Sarah M.",
    role: "BMT Graduation Family",
    base: "Lackland AFB",
    quote: "Rick made our son's BMT Graduation weekend unforgettable. He picked us up from the airport and took us directly onto Lackland—no waiting at the gate, no parking hassle. We got to spend every precious moment with our new Airman.",
    rating: 5
  },
  {
    name: "Col. James R. (Ret.)",
    role: "Retired Air Force",
    base: "Randolph AFB",
    quote: "After 25 years in the Air Force, I know professionalism when I see it. Rick's service is top-notch. His DBIDS credentials and knowledge of base protocols made my VIP guest transportation seamless.",
    rating: 5
  },
  {
    name: "Maria & David L.",
    role: "BAMC Patient Family",
    base: "Fort Sam Houston",
    quote: "My husband's medical appointments at BAMC are stressful enough. Rick's calm, reliable service takes one worry off our plate. He knows exactly where to go and always gets us there early.",
    rating: 5
  },
  {
    name: "The Patterson Family",
    role: "BMT Graduation Family",
    base: "Lackland AFB",
    quote: "We flew in from Ohio for our daughter's graduation. Rick was waiting at SAT with a cold water bottle and a smile. He handled everything—base entry, timing, even restaurant suggestions. Worth every penny!",
    rating: 5
  }
];

const MilitaryTransportation = () => {
  const faqs = [
    {
      question: "Does Rick's Black Car Service have official access to JBSA military bases?",
      answer: "Yes. Rick's Black Car Service is an authorized commercial provider with verified access to all major Joint Base San Antonio (JBSA) installations, including Lackland AFB, Fort Sam Houston, and Randolph AFB. We ensure seamless entry for executive travel and family transportation."
    },
    {
      question: "Do your chauffeurs have the necessary credentials for base entry?",
      answer: "Absolutely. Unlike standard ride-share services, all Rick's Black Car chauffeurs who serve JBSA are fully vetted, background-checked, and hold the required passes (e.g., DBIDS credential or similar) to enter the installation without being sponsored, allowing us to pick you up or drop you off right at your designated location on base."
    },
    {
      question: "What information do I need to provide for a pick-up or drop-off on a military base?",
      answer: "To ensure the quickest entry, we require the following details for all passengers at the time of booking: Full Name, Date of Birth, Primary Form of ID (CAC, Driver's License, Passport), and Installation Name (Lackland AFB, Fort Sam Houston/BAMC, Randolph AFB)."
    },
    {
      question: "Can you provide transportation for Lackland BMT Graduation ceremonies?",
      answer: "Yes, we specialize in premium transportation for BMT Graduation families and officials. We monitor all ceremony and guest schedules to ensure you arrive punctually and comfortably right to the designated meeting area, bypassing the hassle of gate entry and parking."
    },
    {
      question: "Are there any special rules I need to know when entering a military base in your vehicle?",
      answer: "While your chauffeur handles the access protocol, all passengers over 16 must be prepared to show a valid government-issued photo ID to Security Forces at the gate upon request. Please follow the chauffeur's instructions regarding base conduct and vehicle checks, which are standard procedure for all commercial vehicles."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/services/military-transportation#service",
    "name": "Military Base Transportation San Antonio | JBSA Access",
    "serviceType": "Military Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "areaServed": [
      {
        "@type": "MilitaryBase",
        "name": "Lackland Air Force Base"
      },
      {
        "@type": "MilitaryBase",
        "name": "Fort Sam Houston"
      },
      {
        "@type": "MilitaryBase",
        "name": "Randolph Air Force Base"
      },
      {
        "@type": "City",
        "name": "San Antonio",
        "addressRegion": "Texas"
      }
    ],
    "description": "Authorized commercial transportation provider with verified access to JBSA military installations including Lackland AFB, Fort Sam Houston, and Randolph AFB. BMT Graduation transportation, BAMC medical appointments, and executive military travel. Fully vetted chauffeurs with DBIDS credentials.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "category": "Military Transportation Service",
    "serviceOutput": "Professional military base transportation with verified access credentials",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Military Transportation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "BMT Graduation Transportation",
            "description": "Premium transportation for families attending Lackland AFB Basic Military Training graduation ceremonies"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Fort Sam Houston / BAMC Transportation",
            "description": "Discreet on-base service for medical appointments at Brooke Army Medical Center and official business"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Randolph AFB Transportation",
            "description": "Professional transportation to and from Randolph Air Force Base for military personnel and families"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Executive Military Transportation",
            "description": "VIP transportation for military officials, government personnel, and visiting dignitaries"
          }
        }
      ]
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/services/military-transportation",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD",
        "price": "Contact for tailored quote"
      },
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEO 
        title="Military Base Transportation San Antonio | JBSA Access" 
        description="Verified JBSA base access to Lackland AFB, Fort Sam Houston & Randolph AFB. BMT Graduation & BAMC transport. Call (210) 296-7409." 
        canonicalPath="/services/military-transportation" 
        schema={[schemaMarkup, faqSchema]} 
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Military Transportation", url: "/services/military-transportation" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <img 
                src={satAirportImg} 
                alt="Military base transportation San Antonio - JBSA access" 
                className="w-full h-full object-cover brightness-50" 
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/70" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 rounded-full px-4 py-2 mb-6">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-primary font-medium">Verified JBSA Base Access</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                Military & Government Transportation
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Authorized Access to Lackland AFB • Fort Sam Houston • Randolph AFB
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <Link to="/request-quote">
                    Request Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Verified Access Badge Section */}
          <section className="py-12 bg-primary/5 border-y border-primary/20">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
                <div className="flex items-center gap-4">
                  <Shield className="w-12 h-12 text-primary" />
                  <div>
                    <p className="text-lg font-semibold text-foreground">Verified Commercial Provider</p>
                    <p className="text-muted-foreground">Pre-Approved JBSA Base Access</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <FileCheck className="w-12 h-12 text-primary" />
                  <div>
                    <p className="text-lg font-semibold text-foreground">DBIDS Credentialed</p>
                    <p className="text-muted-foreground">Background-Checked Chauffeurs</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Award className="w-12 h-12 text-primary" />
                  <div>
                    <p className="text-lg font-semibold text-foreground">Vehicle Compliant</p>
                    <p className="text-muted-foreground">Meets All JBSA Requirements</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Rick's Black Car Service is San Antonio's trusted luxury transportation provider with <strong className="text-foreground">verified, pre-approved access</strong> to all major Joint Base San Antonio (JBSA) installations. Unlike standard ride-share services like Uber or Lyft, our chauffeurs hold the necessary credentials for unescorted commercial entry—allowing us to transport you directly to your destination on base.
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-8">
                    Why Choose Us for Military Base Transportation?
                  </h2>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Verified Base Access:</strong> Pre-approved entry to Lackland AFB, Fort Sam Houston, and Randolph AFB</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Credentialed Chauffeurs:</strong> All chauffeurs serving JBSA are vetted, background-checked, and hold DBIDS passes</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">No Sponsorship Required:</strong> Unlike ride-share, we can enter without being sponsored by base personnel</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Direct On-Base Service:</strong> Pick up and drop off at your exact location on the installation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">24/7 Availability:</strong> Round-the-clock service for early flights, late ceremonies, or urgent needs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Vehicle Compliance:</strong> Our luxury vehicles meet all JBSA inspection and insurance requirements</span>
                    </li>
                  </ul>
                </div>

                {/* Installation Cards */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8 text-center">
                    JBSA Installations We Serve
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="card-luxury border-2 border-primary/20">
                      <CardContent className="p-6 text-center">
                        <MapPin className="w-10 h-10 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Lackland AFB</h3>
                        <p className="text-muted-foreground text-sm mb-4">BMT Graduation ceremonies, official business, family visits</p>
                        <div className="text-primary font-medium mb-3">★ BMT Graduation Specialist</div>
                        <Button asChild size="sm" variant="outline" className="w-full">
                          <Link to="/bmt-graduation">View BMT Graduation Info</Link>
                        </Button>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury border-2 border-primary/20">
                      <CardContent className="p-6 text-center">
                        <MapPin className="w-10 h-10 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Fort Sam Houston</h3>
                        <p className="text-muted-foreground text-sm mb-4">BAMC medical appointments, official business, family transportation</p>
                        <div className="text-primary font-medium mb-3">★ Medical Appointments</div>
                        <Button asChild size="sm" variant="outline" className="w-full">
                          <Link to="/bamc-transportation">View BAMC Transport Info</Link>
                        </Button>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury border-2 border-primary/20">
                      <CardContent className="p-6 text-center">
                        <MapPin className="w-10 h-10 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Randolph AFB</h3>
                        <p className="text-muted-foreground text-sm mb-4">Official business, executive transportation, family visits</p>
                        <div className="text-primary font-medium mb-3">★ Executive Transport</div>
                        <Button asChild size="sm" variant="outline" className="w-full">
                          <Link to="/randolph-afb-transportation">View Randolph AFB Info</Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* BMT Graduation Highlight */}
                <Card className="card-luxury my-12 border-2 border-primary/30 bg-primary/5">
                  <CardContent className="p-10">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      <div className="flex-shrink-0">
                        <Award className="w-20 h-20 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-4">
                          Lackland BMT Graduation Transportation
                        </h2>
                        <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                          We specialize in premium transportation for Basic Military Training graduation families. Our service ensures you arrive punctually and comfortably right to the designated meeting area—<strong className="text-foreground">bypassing the hassle of gate entry and parking</strong>.
                        </p>
                        <ul className="space-y-2 text-muted-foreground mb-6">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            Ceremony schedule monitoring
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            Direct drop-off at graduation venue
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            Return transportation after ceremony
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-primary" />
                            Family celebration dinner transportation
                          </li>
                        </ul>
                        <Button asChild>
                          <Link to="/bmt-graduation">
                            View BMT Graduation Packages
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Booking Requirements */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-6 text-center">
                      Booking Requirements for Base Access
                    </h2>
                    <p className="text-muted-foreground text-lg text-center mb-8">
                      To ensure the smoothest gate entry, we require the following information at the time of booking:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">1</div>
                        <div>
                          <p className="font-semibold text-foreground">Full Name</p>
                          <p className="text-muted-foreground text-sm">Legal name for all passengers</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">2</div>
                        <div>
                          <p className="font-semibold text-foreground">Date of Birth</p>
                          <p className="text-muted-foreground text-sm">Required for each passenger</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">3</div>
                        <div>
                          <p className="font-semibold text-foreground">Primary Form of ID</p>
                          <p className="text-muted-foreground text-sm">CAC, Driver's License, or Passport</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 p-4 bg-background/50 rounded-lg border border-border/50">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">4</div>
                        <div>
                          <p className="font-semibold text-foreground">Installation Name</p>
                          <p className="text-muted-foreground text-sm">Lackland, Fort Sam, or Randolph</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm text-center mt-6">
                      <strong>Note:</strong> All passengers over 16 must have valid government-issued photo ID ready for Security Forces at the gate.
                    </p>
                  </CardContent>
                </Card>

                {/* JBSA Pricing Comparison Table */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4 text-center">
                    JBSA Transportation Pricing
                  </h2>
                  <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
                    Flat-rate pricing with gratuity included. No hidden fees or surprises.
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="border border-border/50 px-4 py-3 text-left font-semibold text-foreground">Pickup Location</th>
                          <th className="border border-border/50 px-4 py-3 text-center font-semibold text-foreground">
                            <div className="flex flex-col items-center">
                              <span>Lackland AFB</span>
                              <span className="text-xs text-muted-foreground font-normal">BMT Graduation</span>
                            </div>
                          </th>
                          <th className="border border-border/50 px-4 py-3 text-center font-semibold text-foreground">
                            <div className="flex flex-col items-center">
                              <span>Fort Sam Houston</span>
                              <span className="text-xs text-muted-foreground font-normal">BAMC</span>
                            </div>
                          </th>
                          <th className="border border-border/50 px-4 py-3 text-center font-semibold text-foreground">
                            <div className="flex flex-col items-center">
                              <span>Randolph AFB</span>
                              <span className="text-xs text-muted-foreground font-normal">AETC HQ</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="hover:bg-muted/30 transition-colors">
                          <td className="border border-border/50 px-4 py-3 font-medium text-foreground">SAT Airport</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$95</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$85</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$95</td>
                        </tr>
                        <tr className="hover:bg-muted/30 transition-colors bg-muted/10">
                          <td className="border border-border/50 px-4 py-3 font-medium text-foreground">Downtown / Riverwalk</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$85</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$75</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$95</td>
                        </tr>
                        <tr className="hover:bg-muted/30 transition-colors">
                          <td className="border border-border/50 px-4 py-3 font-medium text-foreground">Pearl / Hotel Emma</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$80</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$60</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-muted-foreground">Contact Us</td>
                        </tr>
                        <tr className="hover:bg-muted/30 transition-colors bg-muted/10">
                          <td className="border border-border/50 px-4 py-3 font-medium text-foreground">Stone Oak</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$105</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-muted-foreground">Contact Us</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold">$75</td>
                        </tr>
                        <tr className="hover:bg-muted/30 transition-colors">
                          <td className="border border-border/50 px-4 py-3 font-medium text-foreground">Hourly Service</td>
                          <td className="border border-border/50 px-4 py-3 text-center text-primary font-bold" colSpan={3}>$100/hour (3-hour minimum)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>All prices include 20% gratuity</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Verified base access included</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Late-Night/Early Morning Surcharge: +$35 (10PM-5AM)</span>
                    </div>
                  </div>
                  
                  <div className="text-center mt-8">
                    <Button className="btn-luxury" asChild>
                      <Link to="/request-quote">Get a Custom Quote</Link>
                    </Button>
                  </div>
                </div>

                {/* CTA Card */}
                <Card className="card-luxury my-12 border-2 border-primary/20">
                  <CardContent className="p-10 text-center">
                    <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                      Book Your Military Base Transportation
                    </h2>
                    <p className="text-muted-foreground text-lg mb-6">
                      For immediate, personalized assistance or to confirm a booking, contact us today.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="tel:+12102967409">
                          📞 Call 24/7: 210-296-7409
                        </a>
                      </Button>
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <Link to="/request-quote">
                          Request Personalized Quote
                        </Link>
                      </Button>
                    </div>
                    <p className="text-muted-foreground">
                      Base access service requires a tailored quote based on your specific needs.
                    </p>
                  </CardContent>
                </Card>

                {/* Service Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Users className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Who We Serve</h3>
                          <ul className="text-muted-foreground leading-relaxed space-y-2">
                            <li>• BMT Graduation families</li>
                            <li>• Military personnel & families</li>
                            <li>• BAMC patients & visitors</li>
                            <li>• Government officials</li>
                            <li>• Military contractors</li>
                            <li>• Visiting dignitaries</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Clock className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Service Types</h3>
                          <ul className="text-muted-foreground leading-relaxed space-y-2">
                            <li>• Airport to base transfers (SAT)</li>
                            <li>• Base to hotel/residence</li>
                            <li>• Ceremony transportation</li>
                            <li>• Medical appointment service</li>
                            <li>• Multi-day itineraries</li>
                            <li>• Executive VIP transport</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Related Blog Posts */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8 text-center">
                    JBSA Transportation Guides
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="card-luxury hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-2">BMT Graduation Guide</h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          Essential tips for families attending BMT Graduation at Lackland AFB.
                        </p>
                        <Link 
                          to="/blog/bmt-graduation-transportation-guide-lackland-afb" 
                          className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1"
                        >
                          Read Article <ArrowRight className="w-4 h-4" />
                        </Link>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Fort Sam Houston & BAMC</h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          Complete guide to medical appointment transportation at BAMC.
                        </p>
                        <Link 
                          to="/blog/fort-sam-houston-bamc-medical-appointment-transportation" 
                          className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1"
                        >
                          Read Article <ArrowRight className="w-4 h-4" />
                        </Link>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-2">Randolph AFB Guide</h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          Transportation for military personnel and families at Randolph.
                        </p>
                        <Link 
                          to="/blog/randolph-afb-transportation-guide" 
                          className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1"
                        >
                          Read Article <ArrowRight className="w-4 h-4" />
                        </Link>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Military Family Testimonials */}
                <div className="my-16">
                  <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                      Trusted by Military Families
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      Hear from families who've experienced our premium military base transportation service
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {militaryTestimonials.map((testimonial, index) => (
                      <Card key={index} className="card-luxury border-primary/10 hover:border-primary/30 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-3 mb-4">
                            <Quote className="w-8 h-8 text-primary/30 flex-shrink-0" />
                            <p className="text-muted-foreground italic leading-relaxed">
                              "{testimonial.quote}"
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                            <div>
                              <p className="font-semibold text-foreground">{testimonial.name}</p>
                              <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                              <p className="text-xs text-primary">{testimonial.base}</p>
                            </div>
                            <div className="flex gap-0.5">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* FAQ Section */}
                <ServiceFAQ faqs={faqs} title="Military Transportation FAQs" />

                {/* Blog Resource Link */}
                <div className="mt-12 p-6 bg-primary/5 border border-primary/20 rounded-xl text-center">
                  <h3 className="text-lg font-semibold mb-2">Want to Learn More?</h3>
                  <p className="text-muted-foreground mb-4">
                    Read our complete guide on JBSA military base access transportation, including credentials, requirements, and pricing.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/blog/jbsa-military-base-access-transportation-guide" className="inline-flex items-center gap-2">
                      Read the Complete JBSA Guide
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>

                {/* Final CTA */}
                <div className="text-center mt-12">
                  <p className="text-muted-foreground text-lg mb-4">
                    Experience the difference of verified military base access with San Antonio's trusted luxury transportation provider.
                  </p>
                  <Button className="btn-luxury text-lg px-10 py-6" asChild>
                    <a href="tel:+12102967409">
                      <Phone className="w-5 h-5 mr-2" />
                      Call Now: 210-296-7409
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default MilitaryTransportation;
